import _styled, { default as _styled2, default as _styled3, default as _styled4 } from "styled-components";
import * as RadixAccordion from "@radix-ui/react-accordion";
import React from "react";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { AccordionListItem } from "~/features/blocks/inventory/accordion-list/accordion-list-item";
import { padNumber, takeFirst } from "~/features/app/utils";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type AccordionListBlockViewProps<TItem> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getItemValue: (item: TItem) => string;
  hasTopDivider?: boolean | null;
  items?: TItem[] | null;
  mode?: string | null;
  renderItem: (item: TItem, isOpen: boolean, count: string) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
};
export function AccordionListBlockView<TItem>({
  anchor,
  getItemValue,
  hasTopDivider = true,
  items = [],
  mode = "light",
  renderItem,
  sectionProps,
  theme = "white"
}: AccordionListBlockViewProps<TItem>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const accordionItems = items ?? [];
  // Open the first item by default on load.
  const firstItemId = accordionItems[0] ? getItemValue(accordionItems[0]) : "";
  const [activeAccordionId, setActiveAccordionId] = React.useState(firstItemId);

  // The v3 design uses true white surfaces for the "white" theme. The shared
  // theme helper maps "white" to grey-20, so override it here. bg-primary is
  // mode-aware (white in light mode, near-black in dark) so dark-mode accordions
  // stay legible; the "grey" theme is untouched.
  const surfaceBackground = theme === "grey" ? themeStyle.background : {
    "backgroundColor": "var(--bg-primary)"
  };
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.text, surfaceBackground, themeStyle.border]}>
      <Wrap mode={mode as Mode}>
        {/* Pad the top only when a section rule exists, or the box border doubles it. */}
        <_StyledDiv $_css2={[{
        "paddingLeft": "var(--spacing-md)",
        "paddingRight": "var(--spacing-md)",
        "@media (min-width: 1024px)": {
          "paddingLeft": "var(--spacing-xl)",
          "paddingRight": "var(--spacing-xl)"
        }
      }, hasTopDivider && {
        "paddingTop": "var(--spacing-xl)",
        "@media (min-width: 1024px)": {
          "paddingTop": "var(--spacing-3xl)"
        }
      }]}>
          <_StyledDiv2 $_css3={[{
          "borderLeftWidth": "1px",
          "borderRightWidth": "1px",
          "borderTopWidth": "1px",
          "borderBottomWidth": "1px"
        }, themeStyle.border]}>
            <_StyledRadixAccordionRoot defaultValue={firstItemId || undefined} onValueChange={id => setActiveAccordionId(id)} collapsible type="single" $_css4={[{
            "> :not([hidden]) ~ :not([hidden])": {
              "--tw-divide-y-reverse": "0",
              "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
              "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
            }
          }, themeStyle.border]}>
              {accordionItems.map((item, idx) => <React.Fragment key={getItemValue(item) || idx}>
                  {renderItem(item, getItemValue(item) === activeAccordionId, padNumber(idx + 1))}
                </React.Fragment>)}
            </_StyledRadixAccordionRoot>
          </_StyledDiv2>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1ayfvot-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1ayfvot-1"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1ayfvot-2"
})`${p => p.$_css3}`;
var _StyledRadixAccordionRoot = /*#__PURE__*/_styled4(RadixAccordion.Root).withConfig({
  displayName: "accordion-list___StyledRadixAccordionRoot",
  componentId: "sc-1xee59y-0"
})(["", ""], p => p.$_css4);